import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Calendar, ArrowRight, ArrowLeft, Clock, User, Tag, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/App';

// Book promotion component
const BookPromo = () => (
  <div className="bg-gradient-to-br from-primary/20 via-background-paper to-background-paper rounded-2xl p-6 border border-primary/30 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
    <div className="relative z-10">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-5 h-5 text-primary" />
        <span className="text-primary font-semibold text-sm">NEW BOOK</span>
      </div>
      <h3 className="text-xl font-heading font-bold text-white mb-2">The Afternoon Tea Chronicles</h3>
      <p className="text-text-secondary text-sm mb-4">Afternoon tea menus and recipes from around the world</p>
      <a href="https://www.amazon.co.uk" target="_blank" rel="noopener noreferrer">
        <Button className="w-full bg-primary hover:bg-primary-hover text-white rounded-full">
          <ShoppingBag className="w-4 h-4 mr-2" />
          Buy the Book
        </Button>
      </a>
    </div>
  </div>
);

// Sample blog posts (will be replaced with database content)
const SAMPLE_POSTS = [
  {
    slug: 'traditional-english-afternoon-tea',
    title: 'The Art of Traditional English Afternoon Tea',
    excerpt: 'Discover the history and etiquette of the classic English afternoon tea tradition, from the Duchess of Bedford to modern interpretations.',
    content: `The tradition of afternoon tea dates back to the early 1840s when Anna, the 7th Duchess of Bedford, introduced the concept of a light meal between lunch and dinner.

## The History

In the early 19th century, dinner was typically served at 8pm, leaving a long gap after lunch. The Duchess would experience what she described as "a sinking feeling" around 4 o'clock. She began requesting a tray of tea, bread and butter, and cake be brought to her room.

## The Perfect Menu

A traditional afternoon tea consists of three courses, served on a tiered stand:

**Bottom Tier - Savouries**
- Finger sandwiches with crusts removed
- Classic fillings: cucumber, egg mayonnaise, smoked salmon, coronation chicken

**Middle Tier - Scones**
- Plain and fruit scones
- Served with clotted cream and strawberry jam
- The great debate: cream or jam first?

**Top Tier - Sweets**
- Delicate pastries and petit fours
- Mini cakes and tarts
- Macarons and chocolates

## The Etiquette

- Stir your tea gently back and forth, never in circles
- Hold the cup by the handle, don't cradle it
- Eat with your fingers, not a fork
- Break scones by hand, don't cut them

The afternoon tea tradition continues to evolve, but its essence remains: a moment of calm, good company, and delicious treats.`,
    image: 'https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=800&h=500&fit=crop',
    category: 'Traditions',
    author: 'Greg Wixted',
    date: '2025-02-15',
    readTime: '5 min read'
  },
  {
    slug: 'japanese-afternoon-tea',
    title: 'Japanese Afternoon Tea: East Meets West',
    excerpt: 'Explore how Japan has embraced and transformed the afternoon tea tradition with matcha, wagashi, and stunning presentation.',
    content: `Japan has taken the British afternoon tea tradition and transformed it into something uniquely beautiful, blending Eastern aesthetics with Western customs.

## The Japanese Interpretation

Japanese afternoon tea, often called "afternoon tea set" (アフタヌーンティーセット), combines traditional British elements with Japanese ingredients and presentation styles.

## Key Elements

**Matcha Integration**
- Matcha-infused scones
- Matcha lattes alongside English breakfast tea
- Green tea desserts

**Wagashi - Japanese Sweets**
- Seasonal mochi
- Delicate yokan (sweet bean jelly)
- Cherry blossom themed treats in spring

**Presentation**
- Bento-box style arrangements
- Seasonal decorations
- Edible flowers

## Where to Experience It

Tokyo's top hotels have elevated afternoon tea to an art form, with seasonal menus that change throughout the year. The attention to detail is extraordinary.`,
    image: 'https://images.unsplash.com/photo-1564890369478-c89ca6d9cde9?w=800&h=500&fit=crop',
    category: 'World Teas',
    author: 'Greg Wixted',
    date: '2025-02-10',
    readTime: '4 min read'
  },
  {
    slug: 'perfect-scone-recipe',
    title: 'The Perfect Scone: Our Award-Winning Recipe',
    excerpt: 'Master the art of scone-making with our tried and tested recipe that has won us three consecutive awards.',
    content: `After years of perfecting our scone recipe, we're sharing the secrets that have made Cakery Wonderland's scones famous.

## Ingredients

- 450g self-raising flour
- 1 tsp baking powder
- Pinch of salt
- 100g cold butter, cubed
- 75g caster sugar
- 2 large eggs
- 150ml buttermilk
- 1 tsp vanilla extract

## Method

1. **Preheat** your oven to 220°C (200°C fan)

2. **Combine** flour, baking powder, and salt in a large bowl

3. **Rub in** the cold butter until the mixture resembles breadcrumbs

4. **Stir in** the sugar

5. **Mix** eggs, buttermilk, and vanilla in a jug

6. **Make a well** in the dry ingredients and pour in the wet mixture

7. **Bring together** gently - don't overwork!

8. **Roll out** to 2.5cm thick and cut with a 6cm cutter

9. **Bake** for 12-15 minutes until golden

## Top Tips

- Keep everything cold - cold butter is essential
- Don't twist the cutter - push straight down
- Brush with egg wash for a golden top
- Eat within 2 hours for the best experience

Serve warm with clotted cream and your favourite jam. The Devon way is cream first, the Cornish way is jam first - we'll let you decide!`,
    image: 'https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=800&h=500&fit=crop',
    category: 'Recipes',
    author: 'Greg Wixted',
    date: '2025-02-05',
    readTime: '6 min read'
  },
  {
    slug: 'moroccan-mint-tea-ceremony',
    title: 'The Moroccan Mint Tea Ceremony',
    excerpt: 'Journey to Morocco and discover the beautiful tradition of mint tea, a symbol of hospitality and friendship.',
    content: `In Morocco, tea is more than a drink - it's a symbol of hospitality, friendship, and tradition. The preparation and serving of mint tea is a ceremony in itself.

## The Tradition

Moroccan mint tea, known as "Atay" or "Moroccan whiskey" (despite containing no alcohol), is typically prepared by the head of the household and offered to guests as a sign of welcome.

## The Ingredients

- Chinese gunpowder green tea
- Fresh spearmint leaves (nana)
- Sugar (traditionally lots of it!)
- Boiling water

## The Ceremony

1. **Rinse** the teapot with boiling water
2. **Add** green tea and a splash of water, swirl and discard
3. **Add** fresh mint and sugar
4. **Pour** boiling water and steep for 3-4 minutes
5. **Pour** from a height into small glasses to create a froth
6. **Serve** three glasses - tradition says you must drink three!

## The Meaning

"The first glass is as gentle as life, the second is as strong as love, the third is as bitter as death."

This beautiful tradition reminds us that tea ceremonies around the world share a common purpose: bringing people together.`,
    image: 'https://images.unsplash.com/photo-1571934811356-5cc061b6821f?w=800&h=500&fit=crop',
    category: 'World Teas',
    author: 'Greg Wixted',
    date: '2025-01-28',
    readTime: '4 min read'
  }
];

const CATEGORIES = ['All', 'Traditions', 'World Teas', 'Recipes', 'Behind the Scenes'];

// Blog List Page
const BlogListPage = () => {
  const [posts, setPosts] = useState(SAMPLE_POSTS);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // In production, fetch from API
    // fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === 'All' 
    ? posts 
    : posts.filter(post => post.category === selectedCategory);

  return (
    <div className="min-h-screen bg-background pt-16" data-testid="blog-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-gradient py-20">
        <div className="absolute top-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <BookOpen className="w-12 h-12 text-primary mx-auto mb-6 animate-float" />
            <h1 className="text-5xl font-heading font-bold text-white mb-4" data-testid="blog-title">
              The Afternoon Tea Chronicles
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto font-body">
              Stories, recipes, and tea traditions from around the world
            </p>
          </motion.div>
        </div>
      </section>

      {/* Book Promo Banner */}
      <section className="py-6 bg-primary/10 border-y border-primary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 flex-wrap text-center">
            <BookOpen className="w-5 h-5 text-primary" />
            <p className="text-white font-body">
              <span className="font-bold">NEW BOOK:</span> The Afternoon Tea Chronicles - Menus & recipes from around the world
            </p>
            <a href="https://www.amazon.co.uk" target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-primary hover:bg-primary-hover text-white rounded-full">
                Buy Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-background-elevated">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {CATEGORIES.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-background-paper text-white hover:bg-primary/20 border border-primary/30'
                }`}
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Posts */}
            <div className="lg:col-span-2 space-y-8">
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-background-paper rounded-2xl overflow-hidden border border-primary/20 hover:border-primary/40 transition-all group"
                  data-testid={`blog-post-${post.slug}`}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <div className="md:flex">
                      <div className="md:w-2/5">
                        <div className="aspect-video md:aspect-square h-full">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      </div>
                      <div className="md:w-3/5 p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-xs font-medium">
                            {post.category}
                          </span>
                          <span className="text-text-muted text-xs flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                        <h2 className="text-xl font-heading font-bold text-white mb-2 group-hover:text-primary transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-text-secondary font-body text-sm mb-4 line-clamp-2">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-text-muted text-xs">
                            <User className="w-3 h-3" />
                            {post.author}
                            <span>•</span>
                            <Calendar className="w-3 h-3" />
                            {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </div>
                          <span className="text-primary text-sm font-medium flex items-center gap-1">
                            Read more <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Book Promo */}
              <BookPromo />

              {/* About the Author */}
              <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
                <h3 className="text-lg font-heading font-bold text-white mb-4">About the Author</h3>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
                    <User className="w-8 h-8 text-primary" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">Greg Wixted</p>
                    <p className="text-text-muted text-sm">Cookbook Author & Chef</p>
                  </div>
                </div>
                <p className="text-text-secondary text-sm">
                  Award-winning chef and author of "The Afternoon Tea Chronicles", bringing tea traditions from around the world to your table.
                </p>
              </div>

              {/* Categories */}
              <div className="bg-background-paper rounded-2xl p-6 border border-primary/20">
                <h3 className="text-lg font-heading font-bold text-white mb-4">Categories</h3>
                <div className="space-y-2">
                  {CATEGORIES.filter(c => c !== 'All').map(category => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className="block w-full text-left px-3 py-2 rounded-lg text-text-secondary hover:bg-primary/10 hover:text-primary transition-colors text-sm"
                    >
                      <Tag className="w-4 h-4 inline mr-2" />
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Blog Post Detail Page
const BlogPostPage = ({ slug }) => {
  const navigate = useNavigate();
  const post = SAMPLE_POSTS.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen bg-background pt-16 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-heading font-bold text-white mb-4">Post not found</h2>
          <Link to="/blog">
            <Button className="bg-primary hover:bg-primary-hover text-white rounded-full">
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Convert markdown-style content to HTML sections
  const renderContent = (content) => {
    const sections = content.split('\n\n');
    return sections.map((section, index) => {
      if (section.startsWith('## ')) {
        return (
          <h2 key={index} className="text-2xl font-heading font-bold text-white mt-8 mb-4">
            {section.replace('## ', '')}
          </h2>
        );
      }
      if (section.startsWith('**') && section.endsWith('**')) {
        return (
          <h3 key={index} className="text-lg font-semibold text-primary mt-6 mb-2">
            {section.replace(/\*\*/g, '')}
          </h3>
        );
      }
      if (section.startsWith('- ')) {
        const items = section.split('\n').filter(item => item.startsWith('- '));
        return (
          <ul key={index} className="list-disc list-inside text-text-secondary space-y-1 mb-4">
            {items.map((item, i) => (
              <li key={i}>{item.replace('- ', '')}</li>
            ))}
          </ul>
        );
      }
      if (section.match(/^\d+\./)) {
        const items = section.split('\n').filter(item => item.match(/^\d+\./));
        return (
          <ol key={index} className="list-decimal list-inside text-text-secondary space-y-1 mb-4">
            {items.map((item, i) => (
              <li key={i}>{item.replace(/^\d+\.\s*/, '')}</li>
            ))}
          </ol>
        );
      }
      return (
        <p key={index} className="text-text-secondary font-body leading-relaxed mb-4">
          {section}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background pt-16" data-testid={`blog-post-detail-${slug}`}>
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <Link to="/blog" className="inline-flex items-center text-text-secondary hover:text-primary transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to all posts
        </Link>
      </div>

      {/* Hero Image */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="aspect-video rounded-2xl overflow-hidden mb-8">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2">
            {/* Meta */}
            <div className="flex items-center gap-4 mb-4">
              <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm font-medium">
                {post.category}
              </span>
              <span className="text-text-muted text-sm flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl font-heading font-bold text-white mb-4">
              {post.title}
            </h1>

            {/* Author & Date */}
            <div className="flex items-center gap-4 mb-8 pb-8 border-b border-white/10">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-white font-medium">{post.author}</p>
                <p className="text-text-muted text-sm">
                  {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="prose prose-invert max-w-none">
              {renderContent(post.content)}
            </div>

            {/* Book CTA */}
            <div className="mt-12 p-8 bg-gradient-to-br from-primary/20 to-background-paper rounded-2xl border border-primary/30">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-heading font-bold text-white mb-2">
                    Want more recipes & traditions?
                  </h3>
                  <p className="text-text-secondary">
                    Get "The Afternoon Tea Chronicles" - featuring menus and recipes from around the world.
                  </p>
                </div>
                <a href="https://www.amazon.co.uk" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-4">
                    <ShoppingBag className="w-5 h-5 mr-2" />
                    Buy the Book
                  </Button>
                </a>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="space-y-8">
            <BookPromo />
          </aside>
        </div>
      </div>
    </div>
  );
};

// Main Blog Page Component
const BlogPage = () => {
  const { slug } = useParams();
  
  if (slug) {
    return <BlogPostPage slug={slug} />;
  }
  
  return <BlogListPage />;
};

export default BlogPage;
